import styles from "./styles.scss";
import { FC, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { format } from "date-fns";
import { DayPicker } from "react-day-picker";
import {
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
} from "@floating-ui/react";
import clsx from "clsx";
import { ArrowWithTail, Calendar } from "@/assets/icons";
import { TextField } from "@/components/text-field";
import { useClickOutside } from "@/hooks";
import { formatWeekdayName, getIsTargetElementEqualToRef } from "./utils";
import { DatePickerProps } from "./types";
import {
  DATE_FORMAT,
  DATE_PICKER_OFFSET,
  DATE_PICKER_PLACEMENT,
} from "./constants";

export const DatePicker: FC<DatePickerProps> = ({
  selected,
  portalWrapper,
  wrapperClassName,
  labelClassName,
  inputType,
  inputLabel,
  inputPlaceholder,
  isInputDisabled,
  onSelectedDateChange,
  floatingOptions,
  mandatory,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  const { x, y, refs, strategy, update } = useFloating({
    placement: DATE_PICKER_PLACEMENT,
    middleware: [offset(DATE_PICKER_OFFSET), shift(), flip()],
    whileElementsMounted: (reference, floating, update) =>
      autoUpdate(reference, floating, update, { elementResize: false }),
    ...floatingOptions,
  });

  const handleInputClick = () => {
    setIsOpen(true);
  };

  const handleCalendarIconClick = () => {
    setIsOpen(prevState => !prevState);
    update();
  };

  const handleDayClick = (date: Date) => {
    if (date) {
      if (onSelectedDateChange) {
        onSelectedDateChange(date);
      }

      setIsOpen(false);
    }
  };

  const handleDatePickerClose = (event: MouseEvent) => {
    if (getIsTargetElementEqualToRef(event.target, wrapperRef)) {
      return;
    }

    setIsOpen(false);
  };

  useClickOutside(overlayRef, handleDatePickerClose);

  const today = new Date();

  const DatePickerRender = (
    <div
      ref={refs.setFloating}
      className={styles.overlay}
      style={{
        position: strategy,
        top: y ?? "",
        left: x ?? "",
      }}
    >
      <div ref={overlayRef}>
        <DayPicker
          className={styles.wrapper}
          classNames={{
            table: styles.table,
            caption: styles.caption,
            caption_label: styles.captionLabel,
            month: styles.month,
            nav: styles.nav,
            nav_button: styles.navButton,
            nav_button_previous: styles.navButtonPrevious,
            nav_button_next: styles.navButtonNext,
            head_row: styles.headRow,
            head_cell: styles.headCell,
            cell: styles.cell,
            day: styles.day,
            vhidden: styles.vhidden,
          }}
          selected={selected}
          formatters={{ formatWeekdayName }}
          modifiersClassNames={{
            disabled: styles.disabled,
            today: styles.today,
            selected: styles.selected,
          }}
          components={{
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            IconLeft: ArrowWithTail,
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            IconRight: ArrowWithTail,
          }}
          onDayClick={handleDayClick}
          disabled={{ before: today }}
          {...props}
        />
      </div>
    </div>
  );

  const label = mandatory ? (
    <>
      {inputLabel} <span className={styles.asterix}>*</span>
    </>
  ) : (
    inputLabel
  );

  return (
    <div className={wrapperClassName} ref={wrapperRef}>
      <div className={styles.inputWrapper}>
        <TextField
          readOnly
          ref={refs.setReference}
          type={inputType}
          label={label}
          labelClassName={labelClassName}
          placeholder={inputPlaceholder}
          value={selected ? format(selected, DATE_FORMAT) : ""}
          disabled={isInputDisabled}
          onClick={handleInputClick}
        />
        <button
          type="button"
          className={styles.inputIconWrapper}
          onClick={handleCalendarIconClick}
        >
          <Calendar
            className={clsx(styles.inputIcon, isOpen && styles.inputIconActive)}
          />
        </button>
      </div>

      {isOpen &&
        (portalWrapper
          ? createPortal(DatePickerRender, portalWrapper)
          : DatePickerRender)}
    </div>
  );
};
